import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { MessageSquare, LayoutGrid, Camera, Send, ChevronDown, FileText, Receipt, ClipboardList, LayoutDashboard } from 'lucide-react'
import api from '../../services/api'
import { useAuth } from '../../hooks/useAuth'
import { useProjects } from '../../hooks/useProjects.jsx'
import { useAgentChat } from '../../hooks/useAgentChat.jsx'
import AgentFormCard from '../ai/AgentFormCard.jsx'
import { useTranslation } from 'react-i18next'

/**
 * Chat-first mobile shell. A full-screen surface: land in the conversation, pick a project
 * from the bar, and its milestones + quick actions surface inline.
 *
 * THE CHAT IS THE AGENT, not the team thread. On a phone the agent IS the interface — a
 * consultant standing somewhere photographs a receipt and says what it is, and the agent files
 * it. Sending that to colleagues instead meant the one surface built for the smallest screen
 * was the one that could do the least. The floating owl is deliberately absent here: there is
 * one conversation on a phone, already open, and a draggable mascot on top of it is something
 * to move out of the way rather than something to use.
 *
 * Shares useAgentChat with the desktop panel, so the confirm-before-money flow has exactly one
 * implementation.
 */
export default function MobileShell() {
  const { t: tch } = useTranslation('chrome')
  const navigate = useNavigate()
  const { user } = useAuth()
  const { projects } = useProjects()
  const [projectId, setProjectId] = useState(null)
  const [milestones, setMilestones] = useState([])
  const [showObjects, setShowObjects] = useState(true)
  const [text, setText] = useState('')
  const fileRef = useRef()
  const bottomRef = useRef()

  // Default to the first project once loaded.
  useEffect(() => {
    if (projectId == null && projects.length) setProjectId(projects[0].id)
  }, [projects, projectId])

  useEffect(() => {
    if (!projectId) { setMilestones([]); return }
    api.get(`/projects/${projectId}/milestones/`).then(r => setMilestones(r.data || [])).catch(() => setMilestones([]))
  }, [projectId])

  const {
    messages, thinking, files, addFiles, removeFile,
    send, confirmProposal, cancelProposal, isUsed,
  } = useAgentChat({ greeting: `Hi ${user?.full_name?.split(' ')[0] || 'there'} — tell me what you need, or send a photo of a receipt.` })
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }) }, [messages.length, thinking])

  const activeProject = projects.find(p => p.id === projectId)

  // A photo is worth saying something about, but not worth blocking on: an empty message with
  // an attachment is a perfectly good "here, deal with this".
  const onSend = async () => {
    if (!text.trim() && files.length === 0) return
    const t = text
    setText('')
    await send(t)
  }

  return (
    <div className="fixed inset-0 flex flex-col bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-4 pt-3 pb-2 shrink-0">
        <div className="flex items-center justify-between">
          <div>
            <p className="font-bold leading-tight">ProTrack</p>
            <p className="text-[11px] text-indigo-200">Hi {user?.full_name?.split(' ')[0] || 'there'} 👋</p>
          </div>
          <button onClick={() => navigate('/dashboard')} className="text-[11px] bg-white/15 border border-white/30 rounded-lg px-2.5 py-1">
            Full app →
          </button>
        </div>
        {/* Project context bar — inside chat */}
        <div className="mt-2 flex items-center gap-2">
          <select
            value={projectId || ''}
            onChange={e => setProjectId(Number(e.target.value))}
            className="flex-1 text-sm bg-white/15 border border-white/30 rounded-lg px-3 py-1.5 text-white [&>option]:text-gray-800"
          >
            {projects.length === 0 && <option value="">{tch('no_projects_yet')}</option>}
            {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          <button onClick={() => setShowObjects(s => !s)} className="p-1.5 bg-white/15 border border-white/30 rounded-lg">
            <ChevronDown className={`w-4 h-4 transition ${showObjects ? 'rotate-180' : ''}`} />
          </button>
        </div>
      </div>

      {/* Objects strip — milestones + quick actions for the selected project */}
      {showObjects && activeProject && (
        <div className="bg-white border-b border-gray-200 px-3 py-2 shrink-0 max-h-[38vh] overflow-y-auto">
          <div className="flex gap-2 mb-2">
            <QuickLink icon={FileText} label="Deliverables" onClick={() => navigate(`/projects/${projectId}?tab=deliverables`)} />
            <QuickLink icon={Receipt} label="Invoices" onClick={() => navigate(`/projects/${projectId}?tab=invoices`)} />
            <QuickLink icon={ClipboardList} label="Contract" onClick={() => navigate('/contracts')} />
            <QuickLink icon={LayoutDashboard} label="Overview" onClick={() => navigate(`/projects/${projectId}?tab=overview`)} />
          </div>
          {milestones.length === 0 ? (
            <p className="text-xs text-gray-400 italic py-1">{tch('no_milestones_project')}</p>
          ) : milestones.map(m => (
            <button key={m.id} onClick={() => navigate(`/projects/${projectId}?tab=deliverables&highlight=${m.id}`)}
              className="w-full text-left flex items-center justify-between gap-2 border border-gray-100 rounded-lg px-3 py-2 mb-1.5 hover:bg-indigo-50/40">
              <div className="min-w-0">
                <p className="text-sm font-medium text-gray-800 truncate">{m.title}</p>
                <p className="text-[11px] text-gray-500">{m.status || 'Pending'}{m.scope_status ? ` · ${m.scope_status}` : ''}</p>
              </div>
              {(m.payment_amount > 0) && <span className="text-xs font-semibold text-gray-700 shrink-0">${Number(m.payment_amount).toLocaleString()}</span>}
            </button>
          ))}
        </div>
      )}

      {/* Conversation with the agent */}
      <div className="flex-1 overflow-y-auto px-3 py-3 space-y-2">
        {messages.map((m, i) => {
          const mine = m.role === 'user'
          return (
            <div key={i} className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm ${mine
                ? 'bg-indigo-600 text-white rounded-tr-sm'
                : 'bg-white border border-gray-200 text-gray-800 rounded-tl-sm'}`}>
                {m.content && <p className="whitespace-pre-wrap leading-relaxed">{m.content}</p>}

                {m.links?.length > 0 && (
                  <div className="mt-1.5 flex flex-wrap gap-1.5">
                    {m.links.map((l, j) => (
                      <button key={j} onClick={() => navigate(l.href)}
                        className="text-[11px] font-medium text-indigo-700 bg-indigo-50 rounded-lg px-2 py-1">
                        {l.label || 'Open'}
                      </button>
                    ))}
                  </div>
                )}

                {/* Nothing that moves money happens without this being pressed. */}
                {m.proposal && (
                  <div className="mt-2 border-t border-gray-200 pt-2">
                    <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Confirm</p>
                    {m.proposal.summary && <p className="text-xs text-gray-700 mb-2">{m.proposal.summary}</p>}
                    {isUsed(m.proposal.confirm_token) ? (
                      <p className="text-xs text-gray-400 italic">Handled.</p>
                    ) : (
                      <div className="flex gap-2">
                        <button onClick={() => confirmProposal(m.proposal)} disabled={thinking}
                          className="flex-1 text-xs font-semibold bg-emerald-600 text-white rounded-lg py-2 disabled:opacity-50">
                          Confirm
                        </button>
                        <button onClick={() => cancelProposal(m.proposal)} disabled={thinking}
                          className="flex-1 text-xs font-medium border border-gray-200 text-gray-600 rounded-lg py-2 disabled:opacity-50">
                          Cancel
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {/* Signatures and bank details are typed by the person, never by the model. */}
                {m.form && (
                  <div className="mt-2 border-t border-gray-200 pt-2">
                    <AgentFormCard form={m.form} onDone={(msg) => send(msg || 'Done.')} />
                  </div>
                )}
              </div>
            </div>
          )
        })}

        {thinking && (
          <div className="flex justify-start">
            <div className="bg-white border border-gray-200 rounded-2xl rounded-tl-sm px-3 py-2">
              <span className="inline-flex gap-1">
                <i className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                <i className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                <i className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
              </span>
            </div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      {/* Composer. Always present — the agent answers whether or not a project is picked. */}
      <div className="bg-white border-t border-gray-200 px-2.5 pt-2 shrink-0" style={{ paddingBottom: 'max(0.625rem, env(safe-area-inset-bottom))' }}>
        {files.length > 0 && (
          <div className="flex flex-wrap gap-1.5 pb-2">
            {files.map((f, i) => (
              <span key={`${f.name}-${i}`} className="inline-flex items-center gap-1 text-[11px] bg-indigo-50 text-indigo-800 border border-indigo-100 rounded-lg px-2 py-1">
                <span className="max-w-[9rem] truncate">{f.name}</span>
                <button onClick={() => removeFile(i)} className="text-indigo-400 leading-none" aria-label={`Remove ${f.name}`}>×</button>
              </span>
            ))}
          </div>
        )}
        <div className="flex items-end gap-2">
          <input ref={fileRef} type="file" accept="image/*,application/pdf" capture="environment" multiple className="hidden"
            onChange={e => { addFiles(e.target.files); e.target.value = '' }} />
          <button onClick={() => fileRef.current?.click()} title={tch('t_snap_photo')}
            className="p-2 text-gray-500 border border-gray-200 rounded-xl shrink-0"><Camera className="w-5 h-5" /></button>
          <textarea value={text} onChange={e => setText(e.target.value)} rows={1}
            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); onSend() } }}
            placeholder="Ask, or say what to do with a photo…"
            className="flex-1 resize-none text-sm border border-gray-200 rounded-xl px-3 py-2 max-h-24 focus:outline-none focus:ring-2 focus:ring-indigo-500" />
          <button onClick={onSend} disabled={thinking || (!text.trim() && files.length === 0)}
            className="p-2 bg-indigo-600 text-white rounded-xl disabled:opacity-40 shrink-0"><Send className="w-5 h-5" /></button>
        </div>
      </div>

      {/* Bottom nav */}
      <div className="bg-white border-t border-gray-200 grid grid-cols-3 shrink-0">
        <NavTab icon={MessageSquare} label="Chat" active onClick={() => {}} />
        <NavTab icon={LayoutGrid} label="Projects" onClick={() => navigate('/projects')} />
        <NavTab icon={LayoutDashboard} label="Dashboard" onClick={() => navigate('/dashboard')} />
      </div>
    </div>
  )
}

function QuickLink({ icon: Icon, label, onClick }) {
  return (
    <button onClick={onClick} className="flex-1 flex flex-col items-center gap-0.5 py-1.5 rounded-lg bg-gray-50 border border-gray-100 text-gray-600 hover:bg-indigo-50">
      <Icon className="w-4 h-4" />
      <span className="text-[10px] font-medium">{label}</span>
    </button>
  )
}

function NavTab({ icon: Icon, label, active, onClick }) {
  return (
    <button onClick={onClick} className={`flex flex-col items-center gap-0.5 py-2 ${active ? 'text-indigo-600' : 'text-gray-400'}`}>
      <Icon className="w-5 h-5" />
      <span className="text-[10px] font-medium">{label}</span>
    </button>
  )
}
